import moment from 'moment';
import { WorkOrder, WorkOrderGeneralSettings } from '~/workorder/types';

export const validateTime = (time: string): boolean => {
    if (!time || typeof time !== 'string') {
        return false;
    }
    const parts = time.trim().split(':');
    if (parts.length < 2) {
        return false;
    }
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    return (
        !isNaN(hours) &&
        !isNaN(minutes) &&
        hours >= 0 &&
        hours <= 23 &&
        minutes >= 0 &&
        minutes <= 59
    );
};

const applyTimeToMoment = (m: moment.Moment, time: string): moment.Moment => {
    const [hh, mm] = time.split(':').map((part) => parseInt(part, 10));
    return m.clone().hours(hh).minutes(mm).seconds(0).milliseconds(0);
};

export const hasValidWorkOrderHoursCount = (hoursCount: number): boolean => {
    return hoursCount != null && !isNaN(Number(hoursCount)) && Boolean(hoursCount);
};

export const computePlannedStartEnd = (
    baseDate: moment.MomentInput,
    settings: WorkOrderGeneralSettings,
): { plannedStartDate: string; plannedEndDate: string } => {

    const startTime = settings.plannedStartDate;
    const endTime = settings.plannedEndDate;
    const base = moment(baseDate);
    let start = base.clone();
    let end = base.clone();

    if (hasValidWorkOrderHoursCount(settings.hoursCount)) {
        if (startTime && validateTime(startTime)) {
            start = applyTimeToMoment(start, startTime);
        }
        end = start.clone().add(Number(settings.hoursCount) * 60, 'minutes');
    } else if (
        startTime &&
        endTime &&
        validateTime(startTime) &&
        validateTime(endTime)
    ) {
        start = applyTimeToMoment(start, startTime);
        end = applyTimeToMoment(end, endTime);
    } else {
        start = base.clone();
        end = base.clone().add(60, 'minutes');
    }

    return {
        plannedStartDate: start.format('YYYY-MM-DD[T]HH:mm'),
        plannedEndDate: end.format('YYYY-MM-DD[T]HH:mm'),
    };
};

export type WorkOrderCreatedAtGroup = 'today' | 'thisWeek' | 'older';

const GROUP_ORDER: WorkOrderCreatedAtGroup[] = ['today', 'thisWeek', 'older'];

export const getWorkOrderCreatedAtGroup = (createdAt: string): WorkOrderCreatedAtGroup => {
    const created = moment(createdAt);
    if (!created.isValid()) {
        return 'older';
    }
    const now = moment();
    if (created.isSame(now, 'day')) {
        return 'today';
    }
    if (created.isSameOrAfter(now.clone().startOf('week'), 'day')) {
        return 'thisWeek';
    }
    return 'older';
};

export const groupWorkOrdersByCreatedAt = (
    list: WorkOrder[],
): { group: WorkOrderCreatedAtGroup; items: WorkOrder[] }[] => {
    const buckets: Record<WorkOrderCreatedAtGroup, WorkOrder[]> = {
        today: [],
        thisWeek: [],
        older: [],
    };
    list.forEach(item => {
        buckets[getWorkOrderCreatedAtGroup(item.createdAt)].push(item);
    });
    return GROUP_ORDER
        .filter(group => buckets[group].length > 0)
        .map(group => ({ group, items: buckets[group] }));
};

const formatDayMonth = (date: string): string => moment(date).format('D. MMM');

export const formatTaskHoursLogDate = (date?: string | null): string | null => {
    if (!date) {
        return null;
    }
    const parsed = moment(date);
    return parsed.isValid() ? formatDayMonth(date) : null;
};

export const getWorkOrderTaskUserTotalHours = (
    user: { ordinaryHours: number; overTime50: number; overTime100: number },
): number => Number(user.ordinaryHours) + Number(user.overTime50) + Number(user.overTime100);

const formatDayMonthTime = (date: string): string => moment(date).format('DD. MMM HH:mm');

const formatDayMonthYearTime = (date: string): string => moment(date).format('DD. MMM YY HH:mm');

export const formatWorkOrderDateRange = (
    start?: string,
    end?: string,
): string | null => {
    if (!start && !end) {
        return null;
    }
    if (start && end) {
        const isSameDay = moment(start).isSame(end, 'day');
        if (isSameDay) {
            // Show both times if both on the same date
            return `${formatDayMonthTime(start)} – ${moment(end).format('HH:mm')}`;
        }
        // Different days, show only date
        return `${formatDayMonth(start)} – ${formatDayMonth(end)}`;
    }
    if (start) {
        return formatDayMonth(start);
    }
    return end ? formatDayMonth(end) : null;
};

const formatDayMonthYear = (date: string): string => moment(date).format('DD. MMM YYYY');

export const formatWorkOrderDetailDate = (date?: string | null): string | null => {
    if (!date) {
        return null;
    }
    const parsed = moment(date);
    return parsed.isValid() ? formatDayMonthYearTime(date) : null;
};

export const formatWorkOrderDetailDateRange = (
    start?: string,
    end?: string,
): string | null => {
    if (!start && !end) {
        return null;
    }
    if (start && end) {
        const startFormatted = formatDayMonthYearTime(start);
        const endFormatted = formatDayMonthYearTime(end);
        if (startFormatted && endFormatted) {
            return `${startFormatted} – ${endFormatted}`;
        }
    }
    if (start) {
        return formatDayMonthYearTime(start);
    }
    return end ? formatDayMonthYearTime(end) : null;
};

export const formatWorkOrderHistoryDateTime = (date?: string | null): string | null => {
    if (!date) {
        return null;
    }
    const parsed = moment(date);
    return parsed.isValid() ? parsed.format('DD. MMM YYYY HH:mm') : null;
};

export const resolveOptionLabel = (
    options: { text: string; value: string | number }[],
    id: string | number | null | undefined,
): string | null => {
    if (id === null || id === undefined || id === '') {
        return null;
    }
    const match = options.find(option => Number(option.value) === Number(id));
    return match?.text ?? null;
};

export const isWorkOrderAttachmentImage = (mimeType: string): boolean => {
    return mimeType === 'image/jpeg' || mimeType === 'image/png' || mimeType === 'image/gif' || mimeType === 'image/bmp' || mimeType === 'image/tiff' || mimeType === 'image/webp';
};

export const isWorkOrderAttachmentPdf = (mimeType: string): boolean => {
    return mimeType === 'application/pdf';
};